import { useCallback, useRef } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import Container from '@components/global/Container';
import Heading from '@components/ui/Heading';
import TagManager from 'react-gtm-module';
import { CategoryGridItem } from '@adapters/prismic/types/brandedStorePage';

import styles from './CategoryGrid.module.scss';
import { determinePageType } from '@utils/gtm/ga4/utils';

interface CategoryGridProps {
  title: string;
  items: CategoryGridItem[];
  mobileScroll: boolean;
}

const CategoryGrid = ({ title, items, mobileScroll }: CategoryGridProps) => {
  const ref = useRef<HTMLUListElement | null>(null);

  const trackPromoClick = useCallback(
    (item: CategoryGridItem, index: number) => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'user_interaction',
          event_name: 'click_cat_grid',
          page: {
            page_type: determinePageType(),
          },
          interaction: {
            interaction_location: `item ${index + 1}`,
            link_url: item.url,
            link_text: item.link_text,
          },
        },
      });
    },
    []
  );

  return (
    <Container>
      <Heading styleLevel="2" semanticLevel="h2">
        {title}
      </Heading>
      <ul
        className={cn(styles.categoryGrid, {
          [styles.mobileScroll]: mobileScroll,
        })}
        ref={ref}
      >
        {items.map((item, index) =>
          item.image && item.url && item.link_text ? (
            <li key={item.id} className={styles.item}>
              <a
                href={item.url}
                className={styles.link}
                onClick={() => trackPromoClick(item, index)}
              >
                <div className={styles.imageContainer}>
                  <div
                    className={cn(styles.image, {
                      [styles.mobileScroll]: mobileScroll,
                    })}
                  >
                    <Image
                      src={item.image.url}
                      alt={item.image.alt}
                      fill
                      objectFit="contain"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>
                </div>
                <p className={styles.title}>{item.link_text}</p>
              </a>
            </li>
          ) : null
        )}
      </ul>
    </Container>
  );
};

export default CategoryGrid;
